import { AlertTriangle, Inbox, Lock, WifiOff } from 'lucide-react';
import { Button, EmptyState, Page, Skeleton, Spinner } from '@phinest/ui';
import { RpcError } from '@/lib/rpc';

export function LoadingPage({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-[50dvh] items-center justify-center gap-2 text-[13px] text-text-2">
      <Spinner /> {label}…
    </div>
  );
}

export function LoadingRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full rounded-[var(--radius-sm)]" />
      ))}
    </div>
  );
}

/** Renders any thrown error, with the server's hint when there is one. */
export function ErrorState({ error, onRetry, compact }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const rpc = error instanceof RpcError ? error : null;
  const offline = rpc?.status === 0;
  const denied = rpc?.code === 'permission_denied';
  const icon = offline ? <WifiOff size={18} /> : denied ? <Lock size={18} /> : <AlertTriangle size={18} />;
  const title = offline ? 'Cannot reach the server' : denied ? 'You do not have access to this' : rpc?.message ?? (error instanceof Error ? error.message : 'Something went wrong');
  return (
    <EmptyState
      compact={compact}
      icon={icon}
      title={title}
      description={rpc?.hint ?? (offline ? 'Check that the API is running, then try again.' : undefined)}
      action={onRetry ? <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button> : undefined}
    />
  );
}

export function NothingHere({ title, description, action, icon, compact }: { title: string; description?: string; action?: React.ReactNode; icon?: React.ReactNode; compact?: boolean }) {
  return <EmptyState compact={compact} icon={icon ?? <Inbox size={18} />} title={title} description={description} action={action} />;
}

export function NotFoundPage() {
  return (
    <Page width="narrow">
      <EmptyState
        icon={<AlertTriangle size={18} />}
        title="Page not found"
        description="That link does not point anywhere in this workspace."
        action={
          <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
            Go back
          </Button>
        }
      />
    </Page>
  );
}
